import { useCallback, useEffect, useState } from 'react';
import type { Lang } from '@/data/types';
import { KBIcon } from '@/components/kbIcons';
import type { UndoAction } from '@/lib/actesUndo';
import { actesEn } from '@/lib/actesCountry';

/**
 * Stacked "Annuler" toasts, bottom-left. Several actions can be pending at
 * once and each keeps its own countdown, reverting only itself.
 *
 * Nine seconds is the window: long enough to notice a wrong click on a table
 * row, short enough that the corner does not become a second inbox. A toast
 * being undone right now is never swept away by its own timer.
 */
const LIFETIME_MS = 9000;
const MAX_VISIBLE = 3;

type Toast = UndoAction & { id: number; state: 'idle' | 'undoing' | 'done' | 'error'; error?: string };
let seq = 0;

export function UndoToaster({ lang }: { lang: Lang }) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => setToasts((p) => p.filter((x) => x.id !== id)), []);

  useEffect(() => {
    const onPush = (e: Event) => {
      const a = (e as CustomEvent<UndoAction>).detail;
      if (!a?.undo) return;
      const id = ++seq;
      setToasts((prev) => [{ ...a, id, state: 'idle' as const }, ...prev].slice(0, MAX_VISIBLE));
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((x) => (x.id === id ? x.state === 'undoing' : true)));
      }, LIFETIME_MS);
    };
    window.addEventListener('actes:undo', onPush as EventListener);
    return () => window.removeEventListener('actes:undo', onPush as EventListener);
  }, []);

  const run = async (toast: Toast) => {
    setToasts((p) => p.map((x) => (x.id === toast.id ? { ...x, state: 'undoing' } : x)));
    try {
      await toast.undo();
      setToasts((p) => p.map((x) => (x.id === toast.id ? { ...x, state: 'done' } : x)));
      window.setTimeout(() => dismiss(toast.id), 1400);
    } catch (e) {
      setToasts((p) => p.map((x) => (x.id === toast.id
        ? { ...x, state: 'error', error: e instanceof Error ? e.message : undefined } : x)));
    }
  };

  if (!toasts.length) return null;

  return (
    <div className="actes-undo">
      {toasts.map((t) => (
        <div key={t.id} className={`actes-undo-toast ${t.state}`}>
          <span className="actes-undo-ico">
            <KBIcon name={t.state === 'done' ? 'check' : t.state === 'error' ? 'x' : 'back'} size={14} />
          </span>
          <div className="actes-undo-main">
            <strong>{t.state === 'done' ? T('Annulé', 'Reverted') : t.label}</strong>
            {t.state === 'error'
              ? <em className="bad">{t.error ?? T('Impossible d’annuler', 'Could not revert')}</em>
              : t.detail && <em>{t.detail}</em>}
          </div>
          {t.state !== 'done' && (
            <button className="btn btn-sm" disabled={t.state === 'undoing'} onClick={() => void run(t)}>
              {t.state === 'error' ? T('Réessayer', 'Retry') : t.state === 'undoing' ? '…' : T('Annuler', 'Undo')}
            </button>
          )}
          <button className="actes-undo-x" onClick={() => dismiss(t.id)} aria-label={T('Fermer', 'Dismiss')}>×</button>
        </div>
      ))}
    </div>
  );
}
